<template>
  <div data-smooth-scroll="main">
    <header class="mb-5">
      <h1 class="h3" v-if="heading" v-text="heading" />
      <p class="mb-4" v-if="description" v-text="description" />
      <button type="button" v-focus="focusHeader" class="hidden-header-focus" aria-disabled="true"></button>
      <DrawerButton :active="drawerActive" @clicked="drawerActive ? closeDrawer() : openDrawer()" v-if="helpButtonText">
        <span class="text-primary fw-normal">{{ helpButtonText }}</span>
      </DrawerButton>
    </header>

    <HelpDrawer
      :open="drawerActive"
      :hideHeaderBorder="true"
      class="drawer"
      absolute-close-btn
      show-mobile-backdrop
      @close="closeDrawer()"
      :header="helpDrawer?.header"
      :links="helpDrawer?.links"
      :list-header="helpDrawer?.listHeader"
      :list="helpDrawer?.list"
      :list-footer="helpDrawer?.listFooter"
    >
    </HelpDrawer>

    <div class="mb-3" v-if="showPageNumbers ?? false">Page {{ pageCurrent }} / {{ pageTotal }}</div>

    <div :class="{
      '':                 pageAnimationState === 'waiting',
      'mid-animation':    pageAnimationState !== 'waiting',
      'forward-anim':     pageAnimationState === 'forward',
      'forward-anim-end': pageAnimationState === 'forward-end',
      'back-anim':        pageAnimationState === 'back',
      'back-anim-end':    pageAnimationState === 'back-end'
    }">
      <div class="mb-6">
        <div v-for="(field, index) in applicationFieldsPage" :key="field.alias">
          <BorrowerAttribute
            v-if="field.dependsSatisfied"
            :question="field"
            :update-index="index"
            :disabled="submitting || skipping || transitioning"
            :repress-disabled-style="submitting || skipping || transitioning"
            @answer="answer"
          >
          </BorrowerAttribute>
        </div>
      </div>
      <div v-if="applicationFields && applicationFields.length == 0">
        <!-- No fields matching slug -->
      </div>

      <NavigationButtons
        :shrinkOnSmBack="true"
        :showBack="showBackButton || hasPrevPage"
        :disabledBack="(!showBackButton && !hasPrevPage) || submitting || skipping || transitioning"
        :loadingBack="submitting || skipping"
        dataCyBack="back-app-config"
        @clickBack="closeDrawer(); back()"

        :showNext="showNextButton"
        :loadingNext="submitting || skipping"
        :disabledNext="submitting || skipping || transitioning"
        dataCyNext="next-app-config"
        @clickNext="closeDrawer(); next()"
      >
      </NavigationButtons>

      <transition name="fade">
        <slot name="skipMessage" v-if="v?.$invalid" />
      </transition>
    </div>

  </div>
</template>

<script>
import { dependencySatisfied  } from '~/libs/question-helpers'
import SmoothScroll from '~/libs/smooth-scroll'

import { useApplicationConfigStore } from '~/store/application-config'

import { useVuelidate } from '@vuelidate/core'
import cloneDeep from 'lodash/cloneDeep'
import keyBy from 'lodash/keyBy'

function getPageNext({ applicationFields, pageCurrent }) {
  const applicationField = applicationFields
    .find(x => x.page > pageCurrent && x.dependsSatisfied)
  return applicationField?.page ?? null
}

function getPagePrev({ applicationFields, pageCurrent }) {
  const applicationField = applicationFields
    .slice()
    .reverse()
    .find(x => x.page < pageCurrent && x.dependsSatisfied)
  return applicationField?.page ?? null
}

function getInitialPage({ applicationFields, isBackEvent, route }) {
  // ?page=3
  let page = route.query.page
    ? applicationFields.find(x => x.page == route.query.page)?.page
    : null
  if (page) {
    return page
  }

  // ?alias=foo
  page = route.query.alias
    ? applicationFields.find(x => x.alias === route.query.alias)?.page
    : null
  if (page) {
    return page
  }

  // default logic
  const pageTotal = getPageTotal({ applicationFields })
  return isBackEvent
    ? getPagePrev({ applicationFields, pageCurrent: pageTotal + 1 })
    : getPageNext({ applicationFields, pageCurrent: 0 })
}

function getApplicationFieldsPage({ applicationFields, pageCurrent }) {
  return applicationFields.filter(x => x.page === pageCurrent)
}

function getPageTotal({ applicationFields }) {
  return applicationFields[applicationFields.length - 1]?.page ?? 1
}

function updateDependsSatisfied({ applicationFields }) {
  const fields = applicationFields.map(field => {
    // Ignore modifiers in lookup
    // (e.g. average_monthly_sales@boxSelect should be treated as average_monthly_sales)
    field.alias = field.alias.replace(/@.+$/, '')

    return field
  })

  const lookup = keyBy(fields, 'alias')

  applicationFields.forEach(x => {
    if (!x.dependsOn) {
      return;
    }

    if (!(x.dependsOn in lookup)) {
      // We don't have the parent field, don't try to update the depends on. Trust the backend.
      return;
    }

    x.dependsSatisfied = dependencySatisfied(x, lookup)
  })
}

export default {
  name: 'ApplicationFieldsAppModule',
  props: ['applicationModule', 'skipping', 'isBackEvent', 'showBackButton', 'helpDrawer', 'showNextButton'],
  emits: ['next', 'back', 'postEvent'],
  setup(props) {
    const v = useVuelidate()
    const route = useRoute()
    const applicationConfigStore = useApplicationConfigStore()
    const storeSaveAnswers = applicationConfigStore.saveAnswers
    const storeLoadAnswers = applicationConfigStore.loadAnswersFromStorage

    const helpButtonText = props.helpDrawer?.btnText
    const drawerActive = ref(false)

    const heading = props.applicationModule.displayHeading
    const description = props.applicationModule.displayDescription
    const submitting = ref(false)
    const transitioning = ref(false)
    const pageAnimationState = ref('waiting')
    const applicationFields = reactive(cloneDeep(props.applicationModule.applicationFields) ?? [])
    const pageTotal = getPageTotal({ applicationFields })

    const pageCurrent = ref(getInitialPage({
      isBackEvent: props.isBackEvent,
      applicationFields,
      route,
    }))

    const applicationFieldsPage = ref(
      getApplicationFieldsPage({
        applicationFields,
        pageCurrent: pageCurrent.value
      })
    )
    const unsavedValues = ref({})
    const hasUnsavedValues = computed(() => {
      return Object.keys(unsavedValues.value).length > 0
    })
    const hasPrevPage = computed(() => {
      if (pageTotal <= 1) {
        return false
      }
      if (pageCurrent.value === 0) {
        return false
      }

      return !!getPagePrev({ applicationFields, pageCurrent: pageCurrent.value })
    })

    return {
      v,

      // Actions
      storeSaveAnswers,
      storeLoadAnswers,

      // State
      submitting,
      applicationFields,
      applicationFieldsPage,
      drawerActive,
      helpButtonText,
      pageCurrent,
      pageTotal,
      unsavedValues,
      heading,
      description,
      transitioning,
      pageAnimationState,
      hasPrevPage,
      showPageNumbers: false,

      // Computed
      hasUnsavedValues,
    }
  },
  computed: {
    focusHeader() {
      return this.pageAnimationState === 'forward' || this.pageAnimationState === 'back'
    }
  },
  mounted() {
    this.loadAnswers()
  },
  directives: {
    focus: {
      mounted(el) {
        el.focus()
      },
      updated(el, binding) {
        if (binding.value !== binding.oldValue) {
          el.focus()
        }
      },
    }
  },
  watch: {
    pageCurrent: async function (newVal, oldVal) {
      if (newVal === oldVal) return
      this.transitioning = true
      const duration = 0.3 // Seconds

      let direction = 'forward'
      if (newVal < oldVal) {
        direction = 'back'
      }

      function animationTime(duration) {
        return new Promise(done => setTimeout(done, duration * 1000))
      }

      if (direction === 'forward') {
        this.pageAnimationState = 'forward'
        await animationTime(duration)
        this.updateApplicationFieldsPage()
        this.pageAnimationState = 'forward-end'
        await animationTime(duration)
      } else {
        this.pageAnimationState = 'back'
        await animationTime(duration)
        this.updateApplicationFieldsPage()
        this.pageAnimationState = 'back-end'
        await animationTime(duration)
      }

      this.transitioning = false
      this.pageAnimationState = 'waiting'
    }
  },
  methods: {
    openDrawer() {
      this.drawerActive = true
    },
    closeDrawer() {
      this.drawerActive = false
    },
    updateApplicationFieldsPage() {
      this.applicationFieldsPage = getApplicationFieldsPage({
        applicationFields: this.applicationFields,
        pageCurrent: this.pageCurrent,
      })
    },
    baseAlias(alias) {
      if (typeof alias !== 'string') {
        return alias
      }

      return alias.split('@')[0]
    },
    async answer({ attr, submitImmediately, isTrusted }) {
      if (this.submitting) {
        return
      }

      if (isTrusted === false) {
        return
      }

      const { alias, value } = attr

      // We check if the fields are invalid before saving. No need to check here
      // if (isInvalid) {
      //   delete this.unsavedValues[alias]
      //   return
      // }

      if (Number.isNaN(value) || value === undefined || value === null || value === '') {
        return
      }

      // preserve value for going back
      this.applicationFields.find(x => x.alias === alias).value = value

      // recalculate dependsSatisfied
      updateDependsSatisfied({
        applicationFields: this.applicationFields
      })

      // update ref
      this.updateApplicationFieldsPage()

      // save value to backend
      const pageHasOneQuestion = this.applicationFieldsPage.length === 1

      const baseAlias = this.baseAlias(alias)
      if (submitImmediately && pageHasOneQuestion) {
        this.unsavedValues[baseAlias] = value
        await this.next()
      } else {
        this.unsavedValues[baseAlias] = value
      }
    },
    loadAnswers() {
      const storedAnswers = this.storeLoadAnswers()
      if (Object.keys(storedAnswers).length == 0) {
        return
      }
      this.applicationFields.forEach((applicationField, index) => {
        const alias = this.baseAlias(applicationField.alias)
        if (
          storedAnswers[alias] !== undefined &&
          storedAnswers[alias] !== null
        ) {
          this.applicationFields[index].value = storedAnswers[alias].value
        }
      })
      updateDependsSatisfied({ applicationFields: this.applicationFields })
    },
    async save(attr) {
      const answers = attr
        ? {[attr.alias]: [attr]}
        : this.unsavedValues

      const result = await this.storeSaveAnswers({ answers })

      if (result && Array.isArray(result)) {
        result.forEach(key => delete this.unsavedValues[key])
      } else {
        console.error('FAILED TO SAVE')
      }
    },
    validate() {
      return this.v.$validate()
    },
    async next() {
      // validate
      if (!await this.validate()) {
        return
      }

      this.submitting = true

      // save if anything to save
      if (this.hasUnsavedValues) {
        await this.save()
      }

      SmoothScroll.scrollElementsTop('[data-smooth-scroll="main"]')

      // determine next page
      const pageNext = getPageNext({
        applicationFields: this.applicationFields,
        pageCurrent: this.pageCurrent,
      })

      // move to next page if there is one
      if (pageNext) {
        this.pageCurrent = pageNext
        this.submitting = false
        return
      }

      // last page of questions, emit to parent
      this.$emit('postEvent', 'moduleCompleted')
      return this.$emit('next')
    },
    back() {
      this.transitioning = true
      // determine prev page
      const pagePrev = getPagePrev({
        applicationFields: this.applicationFields,
        pageCurrent: this.pageCurrent,
      })

      SmoothScroll.scrollElementsTop('[data-smooth-scroll="main"]')

      // move to prev page if there is one
      if (pagePrev) {
        this.transitioning = false
        this.pageCurrent = pagePrev
        return
      }

      // first page, emit to parent
      this.$emit('back')
    }
  }

}
</script>

<style lang="scss" scoped>
  $duration: 0.3s;
  .mid-animation {
    opacity: 0;
  }

  @keyframes slide-left-fade-out {
    from {
      transform: translateX(0%);
      opacity: 1;
    }
    80% {
      opacity: 0;
    }
    to {
      transform: translateX(-10%);
      opacity: 0;
    }
  }
  @keyframes slide-left-fade-in {
    from {
      transform: translateX(10%);
      opacity: 0;
    }
    80% {
      opacity: 1;
    }
    to {
      transform: translateX(0%);
      opacity: 1;
    }
  }

  .forward-anim {
    animation-name: slide-left-fade-out;
    animation-duration: $duration;
    animation-direction: normal;
  }
  .forward-anim-end {
    animation-name: slide-left-fade-in;
    animation-duration: $duration;
    animation-direction: normal;
  }
  .back-anim {
    animation-name: slide-left-fade-in;
    animation-duration: $duration;
    animation-direction: reverse;
  }
  .back-anim-end {
    animation-name: slide-left-fade-out;
    animation-duration: $duration;
    animation-direction: reverse;
  }

  .back-btn {
    transition: opacity 0.3s ease-in-out !important;
  }
  .hidden-header-focus {
    opacity: 0;
    display: block;
    border: 0;
    margin: 0;
    padding: 0;
  }
</style>
